import Phaser from "phaser";
import { CANVAS_COLOURS, TIE_STEP, frameId, propDrawRow, reachSortFraction, sortFraction, type AssetManifest, type SortReach, type FrameMaskOf, type OccluderRule, type OrderedProp, type PixelBox, type WorldRect } from "daedalus-core";
import { pageTextureKey } from "./atlas.ts";
import { PropImagePool } from "./prop-image-pool.ts";
import { PropOcclusion, type LiveProp } from "./prop-occlusion.ts";
import { PropShake } from "./prop-shake.ts";
import { PropWithholding } from "./prop-withholding.ts";
import type { ConeSight, CoveringPiece, KeptCover, PieceAlpha } from "./prop-pieces.ts";
import type { TileAnimator } from "./tile-animator.ts";

export type { ConeSight, CoveringPiece, KeptCover, PieceAlpha } from "./prop-pieces.ts";

/**
 * Props as individual images (ADR-021 item 6, ADR-022): one Phaser `Image` per prop of a prop layer, anchored
 * so the frame's bottom-left sits on the bottom-left of cell `(x, y)` (tall frames extend upward), mirrored
 * per `flipX` and `flipY`, and sorted by `layerDepth + sortFraction(propSortRow)` clamped into the layer's
 * band: the row where the art meets the ground (the frame's base, ADR-040) plus the sort offset, so lower
 * props draw over higher ones and a sort offset can pull a part in front. Characters use the same fraction
 * on their feet line (`standingDepth`), so they interleave with props correctly. A frame the manifest marks `ground` (paths,
 * carpets, shadows) sits at the bottom of the band, under every character, unless it is also marked `amongProps` (ADR-040's amendment of 2026-10-06), when it lies flat: it sorts by the top edge of its art like any prop (`propDrawRow`), one image at one depth. A prop that carries a tie key adds
 * `order * TIE_STEP` (P1.6-T42), so props at equal depths draw in one order whichever was added first.
 *
 * Every prop, still or animated, is one whole image at one depth (ADR-132 decision 1): what its footprint blocks never
 * cuts it, and a loop, a transition and a state change swap the frame its one image shows.
 *
 * A view is filled either by a whole list (`sync`, which the editor uses: what the list lacks is removed) or a part
 * at a time (`enter` and `leave`, ADR-019's amendment of 2026-10-03: a game adds the props of the chunks coming into
 * its loaded range and removes those of the chunks going out, and touches no other prop). A view made with a pool keeps
 * the images of removed props, hidden, up to that many, and gives them to the props added next, so a chunk crossing
 * neither destroys nor creates images: destroying one searches the scene's display list, which is what a crossing in
 * a dense place spent most of its time on. A pooled image stays in the display list, hidden, and is in none of the
 * view's walks.
 */

/**
 * Draws the props of one layer.
 */
export class PropView {
  private readonly live = new Map<string, LiveProp>();
  private readonly held: PropWithholding;
  private readonly shaken: PropShake;
  private readonly images: PropImagePool;
  private changes = 0;
  private readonly occlusion: PropOcclusion;

  /**
   * What the view does with a prop whose frame the manifest lacks: null, the default, throws; a function is told
   * the prop, which the view then does not draw. An editor whose atlas is older than its document sets it and
   * draws a box in the prop's place.
   */
  public missing: ((prop: OrderedProp) => void) | null = null;

  /**
   * @param scene Scene to draw into.
   * @param manifest Asset manifest.
   * @param tileSize Tile size in pixels.
   * @param mapHeight Map height in tiles, for depth sorting.
   * @param animator The tile animator looping frames follow, or null.
   * @param coneDims Whether a cone of sight may dim this view's props (ADR-061's amendment for P1.9-T82): false for a
   *   view of props a player uses, which the cone passes over and its cover does not count, as it passes over a
   *   `noFade` frame, and whose props therefore still hide what stands behind them in the cone (`mostCovered`'s kept
   *   share); the occluder fade is unchanged, and so is `markKept`, which counts `noFade` frames alone.
   * @param pool The most images of removed props the view keeps, hidden, for the props added next; 0 destroys every
   *   removed prop's image.
   * @param reach For a view of a neighbouring map's props drawn beside the map (ADR-147 decision 6), placed in the map's
   *   tiles: the rows beyond the map that are drawn, which its props sort over by `reachSortFraction`; null for the map's
   *   own props.
   */
  public constructor(
    scene: Phaser.Scene,
    private readonly manifest: AssetManifest,
    private readonly tileSize: number,
    private readonly mapHeight: number,
    private readonly animator: TileAnimator | null = null,
    coneDims = true,
    pool = 0,
    private readonly reach: SortReach | null = null,
  ) {
    this.images = new PropImagePool(scene, manifest, pool);
    this.held = new PropWithholding(scene);
    this.shaken = new PropShake(scene);
    this.occlusion = new PropOcclusion(this.live, manifest, tileSize, coneDims, () => {
      this.changes += 1;
    });
  }

  /**
   * Number of hidden images the view keeps for the props added next.
   *
   * @returns The count.
   */
  public get pooled(): number {
    return this.images.count;
  }

  /**
   * Number of prop images alive.
   *
   * @returns The count.
   */
  public get count(): number {
    return this.live.size;
  }

  /**
   * A count that moves whenever what the view draws over a character could have changed (ADR-061's amendment for
   * P1.9-T73): a prop added, removed, moved, reframed, flipped, shown or hidden, or sorted to another depth by `sync`,
   * an occluder alpha the fade or `restoreAlpha` changed, and the alpha a prop draws at for the cone leaving 1 or
   * coming back to it (the amendments for P1.9-T75 and P1.9-T82), never a step of an ease between them, since a prop
   * is see-through from the first step below 1. A sync, a fade or a shown frame that changes none of these leaves it, so a caller that caches
   * what the props cover asks again only when the count moves.
   *
   * @returns The count.
   */
  public get version(): number {
    return this.changes;
  }

  /**
   * How many props the cone of sight held below full alpha at the last fade, for the report.
   *
   * @returns The count.
   */
  public get dimmed(): number {
    return this.occlusion.dimmed;
  }

  /**
   * Withholds a prop from the view, or draws it again (`PropWithholding`): a withheld prop's image is hidden while the
   * prop is in the view and stays hidden if its chunk leaves and enters again, until the prop is drawn again. A game
   * uses it for a prop that is gone for now (a harvested one), and its view's layers are always shown.
   *
   * @param id The prop's id.
   * @param withheld True to hide the prop, false to draw it.
   * @param fadeMs How long the ease takes, in milliseconds; 0 for at once.
   */
  public setWithheld(id: string, withheld: boolean, fadeMs = 0): void {
    const entry = this.live.get(id);
    if (this.held.set(id, withheld, fadeMs, entry === undefined ? null : entry.image, entry === undefined ? 1 : Math.min(entry.cover, entry.shade)) && entry !== undefined) {
      this.changes += 1;
    }
  }

  /**
   * Shakes a prop's image a few pixels across and back, for a game that acts on the prop (`PropShake`). A prop the view
   * does not draw, or one that is hidden, shakes nothing. The image ends exactly where the prop stands, and a prop that
   * moves or leaves the view mid-shake is put back first.
   *
   * @param id The prop's id.
   * @param pixels The widest swing, in whole pixels.
   * @param durationMs How long the shake lasts.
   * @returns Whether the prop shakes.
   */
  public shake(id: string, pixels: number, durationMs: number): boolean {
    const entry = this.live.get(id);
    return entry !== undefined && entry.image.visible && this.shaken.start(id, entry.image, entry.x * this.tileSize, pixels, durationMs);
  }

  /**
   * Brings the images in line with a prop list: adds new props, moves, reframes or flips changed ones,
   * removes missing ones, and tints the selected ones.
   *
   * @param props The layer's props, each with its tie key where the caller knows it.
   * @param layerDepth Depth of the layer.
   * @param visible Whether the layer is shown.
   * @param selection The selected prop's id, or whether a prop id is selected when several are (the props
   *   gathered for a template, ADR-066), or null for none.
   * @throws Error when a prop's frame is missing from the manifest and `missing` is null.
   */
  public sync(props: readonly OrderedProp[], layerDepth: number, visible: boolean, selection: string | ((propId: string) => boolean) | null = null): void {
    const seen = new Set<string>();
    for (const prop of props) {
      seen.add(prop.id);
      this.place(prop, layerDepth, visible, typeof selection === "function" ? selection(prop.id) : prop.id === selection);
    }
    for (const [id, entry] of this.live) {
      if (!seen.has(id)) {
        this.retire(entry);
      }
    }
  }

  /**
   * Adds a run of props to the view, or brings those it already draws in line, leaving every other prop as it is: the
   * props of a chunk coming into the loaded range, a slice at a time. Each takes a pooled image when there is one.
   *
   * @param props The list the run is in, each prop with its tie key where the caller knows it.
   * @param from Index of the run's first prop.
   * @param to Index after its last.
   * @param layerDepth Depth of the layer.
   * @param visible Whether the layer is shown.
   * @throws Error when a prop's frame is missing from the manifest and `missing` is null; the props before it stay added, and adding the
   *   run again does no harm.
   */
  public enter(props: readonly OrderedProp[], from: number, to: number, layerDepth: number, visible: boolean): void {
    for (let index = from; index < to; index += 1) {
      this.place(props[index] as OrderedProp, layerDepth, visible, false);
    }
  }

  /**
   * Removes a run of props from the view, leaving every other prop as it is: the props of a chunk going out of the
   * loaded range. Each image is hidden and kept for the next prop added while the pool has room, else destroyed. A
   * prop the view does not draw is passed over.
   *
   * @param props The list the run is in.
   * @param from Index of the run's first prop.
   * @param to Index after its last.
   */
  public leave(props: readonly OrderedProp[], from: number, to: number): void {
    for (let index = from; index < to; index += 1) {
      const entry = this.live.get((props[index] as OrderedProp).id);
      if (entry !== undefined) {
        this.retire(entry);
      }
    }
  }

  /**
   * Draws one prop: makes its image when the view has none, from the pool when it holds one, then reframes, moves,
   * flips, tints and sorts it as the prop says. A new image is given its frame before anything else, so it never
   * keeps what the pool last showed. Nothing is touched that has not changed.
   *
   * @param prop The prop, with its tie key where the caller knows it.
   * @param layerDepth Depth of the layer.
   * @param visible Whether the layer is shown.
   * @param selected Whether the prop is tinted as selected.
   * @throws Error when the prop's frame is missing from the manifest and `missing` is null.
   */
  private place(prop: OrderedProp, layerDepth: number, visible: boolean, selected: boolean): void {
    const frame = frameId(prop.frame.tileset, null, prop.frame.tile);
    const rect = this.manifest.frames[frame];
    if (rect === undefined) {
      if (this.missing === null) {
        throw new Error(`asset manifest has no frame ${frame} for prop ${prop.id}`);
      }
      const drawn = this.live.get(prop.id);
      if (drawn !== undefined) {
        this.retire(drawn);
      }
      this.missing(prop);
      return;
    }
    let entry = this.live.get(prop.id);
    if (entry === undefined) {
      entry = { id: prop.id, image: this.images.take(rect.page, frame), x: NaN, y: NaN, frame: "", shown: "", flipX: false, flipY: false, selected: false, animated: false, cover: 1, dim: 1, shade: 1 };
      this.live.set(prop.id, entry);
      this.changes += 1;
    }
    const image = entry.image;
    if (entry.frame !== frame) {
      this.changes += 1;
      this.unfollow(entry);
      entry.frame = frame;
      this.show(entry, frame, rect.page);
    }
    if (entry.x !== prop.x || entry.y !== prop.y) {
      this.changes += 1;
      this.shaken.stop(prop.id);
      image.setPosition(prop.x * this.tileSize, (prop.y + 1) * this.tileSize);
      entry.x = prop.x;
      entry.y = prop.y;
    }
    if (entry.flipX !== prop.flipX || entry.flipY !== prop.flipY) {
      this.changes += 1;
      image.setFlip(prop.flipX, prop.flipY);
      entry.flipX = prop.flipX;
      entry.flipY = prop.flipY;
    }
    if (entry.selected !== selected) {
      if (selected) {
        image.setTint(CANVAS_COLOURS.selectedProp.rgb);
      } else {
        image.clearTint();
      }
      entry.selected = selected;
    }
    const row = propDrawRow(prop, rect, this.tileSize);
    const depth = layerDepth + (this.reach === null ? sortFraction(row, this.mapHeight) : reachSortFraction(row, this.mapHeight, this.reach)) + (prop.order ?? 0) * TIE_STEP;
    const shown = visible && !this.held.has(prop.id);
    if (image.depth !== depth || (image.visible !== shown && !this.held.fading(prop.id))) {
      this.changes += 1;
      image.setDepth(depth);
      image.setVisible(shown);
    }
  }


  /**
   * Takes a prop out of the view: it stops following its animation, and its image goes to the pool, hidden and as a
   * new image is (unflipped, untinted, at full alpha), while the pool has room, else it is destroyed.
   *
   * @param entry The live prop.
   */
  private retire(entry: LiveProp): void {
    this.changes += 1;
    this.held.stop(entry.id);
    this.shaken.stop(entry.id);
    this.unfollow(entry);
    this.live.delete(entry.id);
    this.images.release(entry);
  }

  /**
   * The boxes of the selected props that are shown, in world pixels: each image's frame standing on its
   * anchor, the frame on screen (a state shown over the prop included), for the map's selection box.
   *
   * @param out The list the boxes are appended to.
   */
  public selectedBoxes(out: WorldRect[]): void {
    for (const entry of this.live.values()) {
      const rect = this.manifest.frames[entry.shown];
      if (entry.selected && entry.image.visible && rect !== undefined) {
        const left = entry.x * this.tileSize;
        const bottom = (entry.y + 1) * this.tileSize;
        out.push({ left, top: bottom - rect.h, right: left + rect.w, bottom });
      }
    }
  }

  /**
   * Shows a frame on a live prop without changing the prop (ADR-058 decision 5): a transition animation
   * plays over a thing this way, one call per render frame, and showing the prop's own frame again returns
   * it to its placed art and whatever loop that art runs. Showing the frame already on screen does
   * nothing, so a call per frame costs one comparison while nothing changes.
   *
   * @param id The prop's id.
   * @param frame The frame id to show.
   * @throws Error when the prop is live, the frame is missing from the manifest and `missing` is null; with
   *   `missing` set the prop keeps the frame it shows.
   */
  public showFrame(id: string, frame: string): void {
    const entry = this.live.get(id);
    if (entry === undefined || entry.shown === frame) {
      return;
    }
    const rect = this.manifest.frames[frame];
    if (rect === undefined) {
      if (this.missing === null) {
        throw new Error(`asset manifest has no frame ${frame} for prop ${id}`);
      }
      return;
    }
    this.unfollow(entry);
    this.show(entry, frame, rect.page);
  }

  /**
   * Puts a frame on an entry's image, following its looping animation when it starts one.
   *
   * @param entry The live prop.
   * @param frame The frame id.
   * @param page The frame's atlas page.
   */
  private show(entry: LiveProp, frame: string, page: number): void {
    const follows = this.animator?.animates(frame) === true;
    entry.image.setTexture(pageTextureKey(this.manifest, page), frame);
    if (follows) {
      (this.animator as TileAnimator).follow(frame, entry.image);
    }
    entry.shown = frame;
    entry.animated = follows;
  }

  /**
   * Stops an entry's image following its animation, if it was.
   *
   * @param entry The live prop.
   */
  private unfollow(entry: LiveProp): void {
    if (entry.animated) {
      this.animator?.unfollow(entry.shown, entry.image);
      entry.animated = false;
    }
  }

  /**
   * Fades every prop that hides most of a rectangle while sorting in front of it, and restores the rest, and eases
   * the cone of sight's alphas in the same walk (`PropOcclusion.fadeOccluders`, ADR-040, ADR-061's amendments for
   * P1.9-T75 and P1.9-T82).
   *
   * @param box Rectangle in world pixels, `x0, y0` top-left, `x1, y1` bottom-right inclusive, whole pixels.
   * @param depth Depth of what stands behind; props at or below it are not occluders.
   * @param rule The least height and share for a prop to fade, and its alpha when it does.
   * @param maskOf The solid pixels of a frame, by frame id.
   * @param exempt Whether a prop, by id, is never faded; absent, none is.
   * @param sight The cone of sight to fade props in as well; absent, none.
   * @returns How many props are faded over the rectangle now.
   * @throws RangeError when the rectangle is empty or off whole pixels and a prop reaches the share test.
   */
  public fadeOccluders(box: PixelBox, depth: number, rule: OccluderRule, maskOf: FrameMaskOf, exempt?: (id: string) => boolean, sight?: ConeSight): number {
    return this.occlusion.fadeOccluders(box, depth, rule, maskOf, exempt, sight);
  }

  /**
   * The largest share of a rectangle one shown, whole prop hides while sorting in front of it (`PropOcclusion.mostCovered`).
   *
   * @param box Rectangle in world pixels, `x0, y0` top-left, `x1, y1` bottom-right inclusive, whole pixels.
   * @param depth Depth of what stands behind; props at or below it are not counted.
   * @param minHeight The least height in pixels a prop's art must rise above its base to count.
   * @param maskOf The solid pixels of a frame, by frame id.
   * @param from The share to beat, for a caller folding several views; 0 for the first.
   * @param kept The share props the cone never dims give, raised in place; absent, it is not measured.
   * @returns The largest share in `[0, 1]`, or `from` when no prop beats it.
   * @throws RangeError when the rectangle is empty or off whole pixels and a prop reaches the share test.
   */
  public mostCovered(box: PixelBox, depth: number, minHeight: number, maskOf: FrameMaskOf, from = 0, kept?: KeptCover): number {
    return this.occlusion.mostCovered(box, depth, minHeight, maskOf, from, kept);
  }

  /**
   * The area of the art of the props a cone of sight could dim that lies inside a rectangle (`PropOcclusion.coneCover`).
   *
   * @param left The rectangle's left edge in world pixels.
   * @param top Its top edge.
   * @param right Its right edge.
   * @param bottom Its bottom edge.
   * @param minHeight The least height in pixels a prop's art must rise above its base to count.
   * @returns The summed area.
   */
  public coneCover(left: number, top: number, right: number, bottom: number, minHeight: number): number {
    return this.occlusion.coneCover(left, top, right, bottom, minHeight);
  }

  /**
   * Marks the pixels of a rectangle that the props that never fade hide (`PropOcclusion.markKept`).
   *
   * @param box Rectangle in world pixels, `x0, y0` top-left, `x1, y1` bottom-right inclusive, whole pixels.
   * @param depth Depth of what stands behind; props at or below it are not counted.
   * @param maskOf The solid pixels of a frame, by frame id.
   * @param marks One byte per pixel of the rectangle, row-major, 1 where a prop already hides it; written in place.
   * @returns How many pixels the view newly marked.
   * @throws RangeError when the rectangle is empty or off whole pixels, or `marks` is smaller than it, and a prop
   *   reaches the pixels.
   */
  public markKept(box: PixelBox, depth: number, maskOf: FrameMaskOf, marks: Uint8Array): number {
    return this.occlusion.markKept(box, depth, maskOf, marks);
  }

  /**
   * Undoes the occluder fade (`PropOcclusion.restoreAlpha`).
   */
  public restoreAlpha(): void {
    this.occlusion.restoreAlpha();
  }

  /**
   * The shown props that cover a character (`PropOcclusion.covering`).
   *
   * @param body The character's body box in world pixels, whole pixels, every edge inclusive.
   * @param standing The character's depth.
   * @param maskOf The solid pixels of a frame, by frame id.
   * @returns The covering pieces.
   * @throws RangeError when the box is empty or off whole pixels and a prop draws above the character.
   */
  public covering(body: PixelBox, standing: number, maskOf: FrameMaskOf): CoveringPiece[] {
    return this.occlusion.covering(body, standing, maskOf);
  }

  /**
   * Every shown prop with the alpha the occluder fade leaves it at (`PropOcclusion.alphas`).
   *
   * @returns The pieces and their alphas.
   */
  public alphas(): PieceAlpha[] {
    return this.occlusion.alphas();
  }

  /**
   * Removes every image, the pooled ones too.
   */
  public clear(): void {
    this.changes += 1;
    for (const entry of this.live.values()) {
      this.unfollow(entry);
      entry.image.destroy();
    }
    this.live.clear();
    this.images.clear();
  }
}
